import React from 'react';
import { 
  Box, 
  Container, 
  Flex, 
  Text, 
  Link, 
  Stack, 
  HStack 
} from '@chakra-ui/react';
import { NavItem, FooterNavGroup } from '../types';
import NavLink from './NavLink';

/**
 * Props for the FooterPresentation component
 */
export interface FooterPresentationProps {
  /** Navigation groups for footer sections */
  navGroups: FooterNavGroup[];
  
  /** Legal links (privacy policy, terms of service, etc.) */
  legalLinks: NavItem[];
}

/**
 * Presentation component for Footer, responsible only for rendering
 */
const FooterPresentation: React.FC<FooterPresentationProps> = ({ 
  navGroups, 
  legalLinks 
}) => {
  return (
    <Box as="footer" bg="gray.800" color="white" py={10}>
      <Container maxW="container.xl">
        <Flex 
          direction={{ base: 'column', md: 'row' }} 
          justify="space-between" 
          align={{ base: 'center', md: 'flex-start' }}
          gap={8}
        >
          <Box textAlign={{ base: 'center', md: 'left' }}>
            <Text 
              fontSize="2xl" 
              fontWeight="bold" 
              bgGradient="linear(to-r, brand.primary.base, brand.primary.light)"
              bgClip="text"
              mb={2}
            >
              AZ1.AI
            </Text>
            <Text color="gray.400" maxW="300px">
              Pioneering agentic systems development and deployment for enterprise solutions.
            </Text>
            <Text mt={2}>
              <Link href="mailto:1@az1.ai" color="brand.primary.base">1@az1.ai</Link>
            </Text>
          </Box>
          
          <Stack spacing={8} direction={{ base: 'column', md: 'row' }} align={{ base: 'center', md: 'flex-start' }}>
            {navGroups.map((group, groupIndex) => (
              <Stack key={groupIndex} spacing={4} align={{ base: 'center', md: 'flex-start' }}>
                <Text fontWeight="bold" fontSize="lg" color="brand.primary.base">{group.title}</Text>
                {group.links.map((link, linkIndex) => (
                  <NavLink 
                    key={linkIndex} 
                    path={link.path}
                    isExternal={link.external}
                  >
                    {link.name}
                  </NavLink>
                ))}
              </Stack>
            ))}
          </Stack>
        </Flex>
        
        <Box mt={12} pt={6} borderTopWidth="1px" borderColor="gray.700">
          <Flex 
            direction={{ base: 'column', md: 'row' }} 
            justify="space-between" 
            align="center"
            gap={4}
          >
            <Text color="gray.400" fontSize="sm">
              © {new Date().getFullYear()} AZ1.AI. All rights reserved.
            </Text>
            
            <HStack spacing={4}>
              {legalLinks.map((link, index) => (
                <NavLink 
                  key={index}
                  path={link.path}
                  isExternal={link.external}
                >
                  {link.name}
                </NavLink>
              ))}
            </HStack>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
};

export default FooterPresentation;